import React, { useState, useEffect } from 'react';
import activityLogService from '../../services/activityLogService';
import Toast from '../../components/Common/Toast';

const UserPerformanceReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
  });

  const loadPerformance = async (override = {}) => {
    setLoading(true);
    const f = { ...filters, ...override };
    try {
      const params = {};
      if (f.date_from) params.date_from = f.date_from;
      if (f.date_to) params.date_to = f.date_to;
      const response = await activityLogService.getPerformance(params);
      if (response.success) {
        setData(response.data || []);
      } else {
        setToast({ isOpen: true, message: response.message || 'Error al cargar el informe', type: 'error' });
      }
    } catch (error) {
      console.error('Error al cargar informe de rendimiento:', error);
      setToast({ isOpen: true, message: 'Error al cargar el informe de rendimiento', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPerformance();
  }, []);

  const handleFilter = (e) => {
    e.preventDefault();
    loadPerformance();
  };

  const totalCreated = data.reduce((s, r) => s + (r.created || 0), 0);
  const totalUpdated = data.reduce((s, r) => s + (r.updated || 0), 0);
  const totalDeleted = data.reduce((s, r) => s + (r.deleted || 0), 0);
  const grandTotal = data.reduce((s, r) => s + (r.total || 0), 0);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Informe de rendimiento por usuario</h1>
        <p className="text-gray-600 mt-1">Resumen de actividad (creaciones, ediciones y eliminaciones) por usuario en el sistema.</p>
      </div>

      <form onSubmit={handleFilter} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((prev) => ({ ...prev, date_from: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((prev) => ({ ...prev, date_to: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700">
              Aplicar
            </button>
            <button
              type="button"
              onClick={() => {
                setFilters({ date_from: '', date_to: '' });
                loadPerformance({ date_from: '', date_to: '' });
              }}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Limpiar
            </button>
          </div>
        </div>
      </form>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Cargando...</div>
        ) : data.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No hay registros de actividad en el periodo seleccionado.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 border-b border-gray-200">
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">Total creaciones</p>
                <p className="text-xl font-bold text-green-600">{totalCreated}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">Total ediciones</p>
                <p className="text-xl font-bold text-blue-600">{totalUpdated}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">Total eliminaciones</p>
                <p className="text-xl font-bold text-red-600">{totalDeleted}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500 uppercase">Total acciones</p>
                <p className="text-xl font-bold text-gray-800">{grandTotal}</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Creaciones</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ediciones</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Eliminaciones</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((row) => (
                    <tr key={row.user_id ?? 'anon'} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{row.user_name || '—'}</div>
                        {row.user_email && <div className="text-xs text-gray-500">{row.user_email}</div>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded text-sm font-medium bg-green-100 text-green-800">
                          {row.created ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded text-sm font-medium bg-blue-100 text-blue-800">
                          {row.updated ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded text-sm font-medium bg-red-100 text-red-800">
                          {row.deleted ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-semibold text-gray-900">{row.total ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
};

export default UserPerformanceReport;
